import { useEffect, useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Download, ExternalLink, RefreshCw } from "lucide-react";
import { AppButton, FigmaTypography } from "@libs/zapi-ui/custom";
import { AppLoader } from "@/zapi-admin/shared/components/AppLoader";
import { JsonViewer } from "@/zapi-admin/shared/components/JsonViewer/ui/JsonViewer";
import { getRouteClaudeRuns } from "@/zapi-admin/shared/const/router";
import { collectClaudeRunLogs, downloadClaudeRunZip, fetchClaudeRun, fetchClaudeRunFile } from "../model/api";
import { RUN_KIND_LABEL, type TClaudeRunDetails } from "../model/types";
import { formatBytes, formatCost, formatDateTime, formatDuration, runDurationMs } from "../lib/format";
import { RunStatusBadge } from "./RunStatusBadge";
import { RunTimeline } from "./RunTimeline";
import { RunFiles } from "./RunFiles";

const LIVE_REFRESH_MS = 5000;
const TRANSCRIPT_FILE = "transcript.jsonl";
const TERMINAL = new Set(["succeeded", "failed", "cancelled"]);

type TTab = "timeline" | "files" | "json";

const isLive = (run: TClaudeRunDetails) => !TERMINAL.has(run.status) || run.logStatus === "collecting";

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <div className="text-[11px] uppercase tracking-wide text-zinc-400">{label}</div>
      <div className="text-[13px] text-zinc-800 wrap-break-word">{children}</div>
    </div>
  );
}

function LogSummary({ run }: { run: TClaudeRunDetails }) {
  if (run.logStatus === "stored") {
    return (
      <>
        {run.logFiles.length} files · {formatBytes(run.logBytes)} · stored {formatDateTime(run.logStoredAt)}
      </>
    );
  }
  if (run.logStatus === "collecting") return <span className="text-zinc-500">being collected from the runner…</span>;
  if (run.logStatus === "failed") {
    return <span className="text-red-700">collection failed{run.logError ? `: ${run.logError}` : ""}</span>;
  }
  return (
    <span className="text-zinc-500">{TERMINAL.has(run.status) ? "not collected" : "available once the run ends"}</span>
  );
}

function Transcript({ run }: { run: TClaudeRunDetails }) {
  const stored = run.logStatus === "stored" && run.logFiles.some((f) => f.name === TRANSCRIPT_FILE);
  const { data, isLoading, error } = useQuery({
    queryKey: ["claude-run-file", run.id, TRANSCRIPT_FILE],
    queryFn: () => fetchClaudeRunFile(run.id, TRANSCRIPT_FILE),
    enabled: stored
  });
  if (!stored) return <div className="text-[13px] text-zinc-500">No transcript is stored for this run.</div>;
  if (isLoading) {
    return (
      <div className="flex justify-center py-10">
        <AppLoader />
      </div>
    );
  }
  if (error || data === undefined)
    return <div className="text-[13px] text-red-700">The transcript could not be loaded.</div>;
  return <RunTimeline text={data} />;
}

export function ClaudeRunPage() {
  const { id = "" } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<TTab>("timeline");
  const [live, setLive] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const {
    data: run,
    isLoading,
    error
  } = useQuery({
    queryKey: ["claude-run", id],
    queryFn: () => fetchClaudeRun(id),
    enabled: Boolean(id),
    refetchInterval: live ? LIVE_REFRESH_MS : false
  });

  useEffect(() => {
    setLive(Boolean(run && isLive(run)));
  }, [run]);

  const collect = useMutation({
    mutationFn: () => collectClaudeRunLogs(id),
    onSettled: () => queryClient.invalidateQueries({ queryKey: ["claude-run", id] })
  });

  const download = async () => {
    setDownloading(true);
    try {
      await downloadClaudeRunZip(id);
    } finally {
      setDownloading(false);
    }
  };

  const tabButton = (key: TTab, label: string) => (
    <AppButton
      appearance={tab === key ? "contained" : "outlined"}
      color="zinc950"
      className="px-4 py-2"
      onClick={() => setTab(key)}
      data-testid={`run-tab-${key}`}
    >
      {label}
    </AppButton>
  );

  return (
    <div className="container mx-auto py-8 pt-[95px] min-h-[calc(100vh-51px-64px)]" data-testid="claude-run-page">
      <Link
        to={getRouteClaudeRuns()}
        className="inline-flex items-center gap-1 text-[13px] text-zinc-500 hover:text-zinc-900 mb-4"
      >
        <ArrowLeft size={14} /> Claude Runs
      </Link>

      {isLoading && (
        <div className="flex justify-center py-10">
          <AppLoader />
        </div>
      )}
      {!isLoading && (error || !run) && <div className="text-[14px] text-red-700">This run could not be loaded.</div>}

      {run && (
        <>
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <FigmaTypography component="h1" variant="heading-1" data-testid="claude-run-heading">
              {RUN_KIND_LABEL[run.kind]}
            </FigmaTypography>
            <RunStatusBadge status={run.status} />
            <span className="font-mono text-[12px] text-zinc-400">{run.id}</span>
            <div className="flex items-center gap-2 ml-auto">
              {run.projectUrl && (
                <a href={run.projectUrl} target="_blank" rel="noreferrer" data-testid="claude-run-open-project">
                  <AppButton appearance="outlined" color="zinc950" className="flex items-center gap-2 px-4 py-2 h-fit">
                    <ExternalLink size={16} /> Open project
                  </AppButton>
                </a>
              )}
              {TERMINAL.has(run.status) && run.logStatus !== "stored" && run.logStatus !== "collecting" && (
                <AppButton
                  appearance="outlined"
                  color="zinc950"
                  className="flex items-center gap-2 px-4 py-2 h-fit"
                  disabled={collect.isPending}
                  onClick={() => collect.mutate()}
                  data-testid="claude-run-collect-logs"
                >
                  <RefreshCw size={16} className={collect.isPending ? "animate-spin" : ""} />
                  Fetch log from runner
                </AppButton>
              )}
              <AppButton
                appearance="contained"
                color="zinc950"
                className="flex items-center gap-2 px-4 py-2 h-fit"
                disabled={run.logStatus !== "stored" || downloading}
                onClick={download}
                data-testid="claude-run-download"
              >
                <Download size={16} /> {downloading ? "Preparing…" : "Download zip"}
              </AppButton>
            </div>
          </div>
          {collect.isError && (
            <div className="mb-4 text-[13px] text-red-700">
              {(collect.error as { response?: { data?: { message?: string } } }).response?.data?.message ??
                "The log could not be fetched from the runner."}
            </div>
          )}

          {run.userMessage && (
            <div className="rounded-[10px] border border-zinc-150 p-4 mb-4" data-testid="claude-run-user-message">
              <div className="text-[11px] uppercase tracking-wide text-zinc-400">User message</div>
              <div className="mt-1 max-h-60 overflow-y-auto text-[14px] text-zinc-900 whitespace-pre-wrap wrap-break-word">
                {run.userMessage}
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-4 rounded-[10px] border border-zinc-150 p-4 mb-4">
            <Field label="Started">{formatDateTime(run.startedAt ?? run.createdAt)}</Field>
            <Field label="Finished">{formatDateTime(run.finishedAt)}</Field>
            <Field label="Duration">{formatDuration(runDurationMs(run))}</Field>
            <Field label="Cost">{formatCost(run.estCostUsd)}</Field>
            <Field label="Model">
              {run.model ?? "—"}{" "}
              <span className="text-zinc-500">
                · mode {run.mode}
                {run.effort && ` · effort ${run.effort}`} · launch {run.launch}
              </span>
            </Field>
            <Field label="Skill">
              {run.skill}
            </Field>
            <Field label="Turns / tool calls">
              {run.assistantTurns ?? "—"} / {run.toolCalls ?? "—"}
            </Field>
            <Field label="Organization">
              <span className="text-zinc-500">{run.organization.id}</span> {run.organization.name}
            </Field>
            <Field label="User">
              {run.user ? (
                <>
                  {run.user.firstName} {run.user.lastName} <span className="text-zinc-500">· {run.user.email}</span>
                </>
              ) : (
                "—"
              )}
            </Field>
            <Field label="Project">
              {run.project ? (
                <>
                  <span className="text-zinc-500">{run.project.id}</span> {run.project.name}
                </>
              ) : run.projectId ? (
                `deleted (${run.projectId})`
              ) : (
                "—"
              )}
            </Field>
            <Field label={run.kind === "chat" ? "Leo chat task" : "Generation task"}>
              {run.chatTask && (
                <>
                  {run.chatTask.canEdit ? "can edit" : "answer only"} · {run.chatTask.status}
                  {Array.isArray(run.chatTask.appliedChapters) && run.chatTask.appliedChapters.length > 0 && (
                    <span className="text-zinc-500"> · {run.chatTask.appliedChapters.length} chapters edited</span>
                  )}
                </>
              )}
              {run.generationTask && (
                <>
                  #{run.generationTask.id} · {run.generationTask.status}
                  {run.generationTask.phase && <span className="text-zinc-500"> · {run.generationTask.phase}</span>}
                </>
              )}
              {!run.chatTask && !run.generationTask && "—"}
            </Field>
            <Field label="Log">
              <LogSummary run={run} />
            </Field>
          </div>

          {run.error && (
            <div
              className="mb-4 rounded-[10px] bg-red-50 p-3 text-[13px] text-red-800 whitespace-pre-wrap"
              data-testid="claude-run-error"
            >
              {run.error}
            </div>
          )}

          <div className="flex gap-2 mb-4">
            {tabButton("timeline", "Timeline")}
            {tabButton("files", "Files")}
            {tabButton("json", "Run JSON")}
          </div>

          {tab === "timeline" && <Transcript run={run} />}
          {tab === "files" && <RunFiles run={run} />}
          {tab === "json" && (
            <div className="h-[70vh]">
              <JsonViewer json={JSON.stringify(run)} />
            </div>
          )}
        </>
      )}
    </div>
  );
}
